'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
    VOICE_MAX_AUDIO_BYTES,
    VOICE_MAX_RECORDING_DURATION_MS,
    VOICE_MIN_AUDIO_BYTES,
} from '../constants';
import { studioAIService } from '../services';

function getSupportedMimeType(): string {
    const types = ['audio/webm', 'audio/ogg'];
    for (const type of types) {
        if (MediaRecorder.isTypeSupported(type)) return type;
    }
    return '';
}

type BlobValidation = 'ok' | 'silent-reject' | 'too-large';

function validateAudioBlob(blob: Blob): BlobValidation {
    if (blob.size < VOICE_MIN_AUDIO_BYTES) return 'silent-reject';
    if (blob.size > VOICE_MAX_AUDIO_BYTES) return 'too-large';
    return 'ok';
}

export function useVoiceInput(
    onTranscribed: (text: string) => void,
    conversationId?: string | null,
) {
    const [isRecording, setIsRecording] = useState(false);
    const [isTranscribing, setIsTranscribing] = useState(false);
    const [recordingDuration, setRecordingDuration] = useState(0);

    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const chunksRef = useRef<Blob[]>([]);
    const streamRef = useRef<MediaStream | null>(null);
    const audioContextRef = useRef<AudioContext | null>(null);
    const analyserRef = useRef<AnalyserNode | null>(null);
    const recordingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const durationTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    // true = transcribe on stop, false = cancel (cleanup only)
    const transcribeOnStopRef = useRef(false);
    const conversationIdRef = useRef(conversationId);
    conversationIdRef.current = conversationId;
    // Prevents concurrent invocations racing through the async setup
    const recordingLockRef = useRef(false);
    // Guards post-await continuations from running after unmount
    const isMountedRef = useRef(true);

    const stopStream = useCallback(() => {
        if (recordingTimerRef.current !== null) {
            clearTimeout(recordingTimerRef.current);
            recordingTimerRef.current = null;
        }
        if (durationTimerRef.current !== null) {
            clearInterval(durationTimerRef.current);
            durationTimerRef.current = null;
        }
        setRecordingDuration(0);
        streamRef.current?.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
        audioContextRef.current?.close();
        audioContextRef.current = null;
        analyserRef.current = null;
    }, []);

    const startRecording = useCallback(async () => {
        if (recordingLockRef.current) return;
        recordingLockRef.current = true;
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            if (!isMountedRef.current) {
                stream.getTracks().forEach((t) => t.stop());
                recordingLockRef.current = false;
                return;
            }
            streamRef.current = stream;

            // Set up Web Audio API for visualizer
            const audioContext = new AudioContext();
            audioContextRef.current = audioContext;
            // Resume if browser suspended the context (common on mobile)
            if (audioContext.state === 'suspended') {
                await audioContext.resume();
                if (!isMountedRef.current) {
                    stream.getTracks().forEach((t) => t.stop());
                    audioContext.close();
                    audioContextRef.current = null;
                    recordingLockRef.current = false;
                    return;
                }
            }
            const analyser = audioContext.createAnalyser();
            analyser.fftSize = 64; // 32 frequency bins — enough for 5 bars
            audioContext.createMediaStreamSource(stream).connect(analyser);
            analyserRef.current = analyser;

            const mimeType = getSupportedMimeType();
            const mediaRecorder = mimeType
                ? new MediaRecorder(stream, { mimeType })
                : new MediaRecorder(stream);

            chunksRef.current = [];

            mediaRecorder.ondataavailable = (e) => {
                if (e.data.size > 0) {
                    chunksRef.current.push(e.data);
                }
            };

            // Set onstop handler once — transcribeOnStopRef controls whether to transcribe or cancel
            mediaRecorder.onstop = async () => {
                if (!transcribeOnStopRef.current) {
                    mediaRecorderRef.current = null;
                    chunksRef.current = [];
                    stopStream();
                    recordingLockRef.current = false;
                    return;
                }
                transcribeOnStopRef.current = false;

                const recordedMimeType = mediaRecorder.mimeType || 'audio/webm';
                const blob = new Blob(chunksRef.current, { type: recordedMimeType });

                switch (validateAudioBlob(blob)) {
                    case 'silent-reject':
                        mediaRecorderRef.current = null;
                        chunksRef.current = [];
                        stopStream();
                        setIsTranscribing(false);
                        recordingLockRef.current = false;
                        return;
                    case 'too-large':
                        toast.error('Recording is too large. Please record a shorter message.', {
                            position: 'top-right',
                            autoClose: 3000,
                        });
                        mediaRecorderRef.current = null;
                        chunksRef.current = [];
                        stopStream();
                        setIsTranscribing(false);
                        recordingLockRef.current = false;
                        return;
                }

                // Transcribe
                try {
                    const MIME_TO_EXT: Record<string, string> = {
                        webm: 'webm',
                        ogg: 'ogg',
                        mp4: 'mp4',
                        m4a: 'm4a',
                        mpeg: 'mp3',
                        wav: 'wav',
                        flac: 'flac',
                    };
                    const subtype = recordedMimeType.split('/')[1]?.split(';')[0] ?? '';
                    const ext = MIME_TO_EXT[subtype] ?? 'webm';
                    const formData = new FormData();
                    formData.append(
                        'file',
                        new File([blob], `recording.${ext}`, { type: recordedMimeType }),
                    );
                    if (conversationIdRef.current) {
                        formData.append('conversationId', conversationIdRef.current);
                    }
                    const result = await studioAIService.transcribeAudio(formData);
                    if (result.text) {
                        onTranscribed(result.text);
                    }
                } catch {
                    toast.error('Failed to transcribe audio. Please try again.', {
                        position: 'top-right',
                        autoClose: 3000,
                    });
                } finally {
                    mediaRecorderRef.current = null;
                    chunksRef.current = [];
                    stopStream();
                    setIsTranscribing(false);
                    recordingLockRef.current = false;
                }
            };

            // Auto-stop after max duration to prevent runaway sessions
            recordingTimerRef.current = setTimeout(() => {
                if (mediaRecorderRef.current?.state !== 'inactive') {
                    recordingTimerRef.current = null;
                    if (durationTimerRef.current !== null) {
                        clearInterval(durationTimerRef.current);
                        durationTimerRef.current = null;
                    }
                    setRecordingDuration(0);
                    transcribeOnStopRef.current = true;
                    setIsRecording(false);
                    setIsTranscribing(true);
                    mediaRecorderRef.current?.stop();
                }
            }, VOICE_MAX_RECORDING_DURATION_MS);

            mediaRecorderRef.current = mediaRecorder;
            mediaRecorder.start();
            setRecordingDuration(0);
            durationTimerRef.current = setInterval(() => {
                setRecordingDuration((prev) => prev + 1);
            }, 1000);
            setIsRecording(true);
        } catch (error) {
            recordingLockRef.current = false;
            stopStream();
            if (error instanceof DOMException && error.name === 'NotFoundError') {
                toast.error('No microphone found. Please connect a microphone and try again.', {
                    position: 'top-right',
                    autoClose: 4000,
                });
            } else if (error instanceof DOMException && error.name === 'NotReadableError') {
                toast.error(
                    'Microphone is in use by another application. Please close it and try again.',
                    { position: 'top-right', autoClose: 4000 },
                );
            } else if (error instanceof DOMException && error.name === 'NotAllowedError') {
                toast.error(
                    'Microphone access denied. Please allow microphone access in your browser settings.',
                    { position: 'top-right', autoClose: 4000 },
                );
            } else {
                toast.error('Could not start recording. Please check your microphone.', {
                    position: 'top-right',
                    autoClose: 3000,
                });
            }
        }
    }, [stopStream, onTranscribed]);

    const cancelRecording = useCallback(() => {
        transcribeOnStopRef.current = false; // onstop will skip transcription
        const recorder = mediaRecorderRef.current;
        if (recorder && recorder.state !== 'inactive') {
            recorder.stop(); // onstop handles cleanup + lock release
        } else {
            mediaRecorderRef.current = null;
            chunksRef.current = [];
            stopStream();
            recordingLockRef.current = false;
        }
        setIsRecording(false);
    }, [stopStream]);

    const stopAndTranscribe = useCallback(() => {
        const recorder = mediaRecorderRef.current;
        if (!recorder || recorder.state === 'inactive' || recorder.state === 'paused') return;
        if (recordingTimerRef.current !== null) {
            clearTimeout(recordingTimerRef.current);
            recordingTimerRef.current = null;
        }
        if (durationTimerRef.current !== null) {
            clearInterval(durationTimerRef.current);
            durationTimerRef.current = null;
        }
        setRecordingDuration(0);
        transcribeOnStopRef.current = true;
        setIsRecording(false);
        setIsTranscribing(true);
        recorder.stop();
    }, []);

    // Release all media and timer resources on unmount
    useEffect(() => {
        return () => {
            isMountedRef.current = false;
            const recorder = mediaRecorderRef.current;
            if (recorder) {
                recorder.ondataavailable = null;
                recorder.onstop = null;
                if (recorder.state !== 'inactive') recorder.stop();
                mediaRecorderRef.current = null;
            }
            chunksRef.current = [];
            stopStream();
            recordingLockRef.current = false;
        };
        // stopStream is stable (no deps), safe to list once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        isRecording,
        isTranscribing,
        recordingDuration,
        analyserRef,
        startRecording,
        cancelRecording,
        stopAndTranscribe,
    };
}
